import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../utils/api';
import { 
  getWeekNumber, 
  getWeekDateRange, 
  getCurrentWeek
} from '../../utils/weekCalculations';

const AdminAttendanceTaskPage = () => {
  const { users = [], sites = [] } = useOutletContext();
  
  const currentWeekData = getCurrentWeek();
  
  const [fromYear, setFromYear] = useState(currentWeekData.year);
  const [fromWeek, setFromWeek] = useState(currentWeekData.week);
  const [toYear, setToYear] = useState(currentWeekData.year);
  const [toWeek, setToWeek] = useState(currentWeekData.week);
  const [selectedUsers, setSelectedUsers] = useState([]);
  
  const [showFromCalendar, setShowFromCalendar] = useState(false);
  const [showToCalendar, setShowToCalendar] = useState(false);
  
  const [performanceDataList, setPerformanceDataList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [modalTaskType, setModalTaskType] = useState('');
  const [modalTasks, setModalTasks] = useState([]);

  const getWeekDates = (year, weekNum) => {
    const { start } = getWeekDateRange(year, weekNum);
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push({ date: date.toISOString().split('T')[0], day: date.getDate() });
    }
    return dates;
  };

  function formatWeekDisplay(year, weekNum) {
    const { startStr, endStr } = getWeekDateRange(year, weekNum);
    return `${year} Week ${weekNum} (${startStr} - ${endStr})`;
  }

  function handleDateClick(date, isFrom = true) {
    const year = date.getFullYear();
    const weekNum = getWeekNumber(date, year);
    
    if (isFrom) {
      setFromYear(year);
      setFromWeek(weekNum);
      setShowFromCalendar(false);
      
      const fromRange = getWeekDateRange(year, weekNum);
      const toRange = getWeekDateRange(toYear, toWeek);
      
      if (fromRange.start > toRange.start) {
        setToYear(year);
        setToWeek(weekNum);
      }
    } else {
      const toRange = getWeekDateRange(year, weekNum);
      const fromRange = getWeekDateRange(fromYear, fromWeek);
      
      if (toRange.start < fromRange.start) {
        setFromYear(year);
        setFromWeek(weekNum);
      }
      
      setToYear(year);
      setToWeek(weekNum);
      setShowToCalendar(false);
    }
  }

  function generateCalendar(currentDate) {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    
    const dayOfWeek = firstDay.getDay();
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    startDate.setDate(startDate.getDate() - daysToSubtract);
    
    const weeks = [];
    let currentWeek = [];
    
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
      currentWeek.push(date);
      
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }
    
    return { weeks, month, year };
  }

  const goToCurrentWeek = () => {
    const { year, week } = getCurrentWeek();
    setFromYear(year);
    setFromWeek(week);
    setToYear(year);
    setToWeek(week);
  };

  useEffect(() => {
    const { year, week } = getCurrentWeek();
    
    // Get the current week's date range to find the actual previous week
    const currentWeekRange = getWeekDateRange(year, week);
    
    // Go back 7 days from the current week's Monday to get the previous week's Monday
    const prevWeekMonday = new Date(currentWeekRange.start);
    prevWeekMonday.setDate(prevWeekMonday.getDate() - 7);
    
    // Calculate the year and week number for the previous week
    const prevYear = prevWeekMonday.getFullYear();
    const prevWeek = getWeekNumber(prevWeekMonday, prevYear);
    
    setFromYear(prevYear);
    setFromWeek(prevWeek);
    setToYear(prevYear);
    setToWeek(prevWeek);
  }, []);

  useEffect(() => {
    if (selectedUsers.length > 0) {
      loadPerformanceData();
    } else {
      setPerformanceDataList([]);
    }
  }, [selectedUsers, fromYear, fromWeek, toYear, toWeek]);

  const loadPerformanceData = async () => {
    setIsLoading(true);
    setPerformanceDataList([]);
    
    try {
      selectedUsers.forEach(async (userId, index) => {
        try {
          const response = await axios.get(`${API}/admin/team-performance`, {
            params: {
              user_id: userId,
              from_year: fromYear,
              from_week: fromWeek,
              to_year: toYear,
              to_week: toWeek
            },
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          
          setPerformanceDataList(prev => [...prev, response.data]);
          
          if (index === selectedUsers.length - 1) {
            setIsLoading(false);
          }
        } catch (error) {
          console.error(`Failed to load performance for user ${userId}:`, error);
          if (index === selectedUsers.length - 1) {
            setIsLoading(false);
          }
        }
      });
    } catch (error) {
      console.error('Failed to load performance data:', error);
      setPerformanceDataList([]);
      setIsLoading(false);
    }
  };

  const handleUserToggle = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(u => u.id));
    }
  };

  // Dark theme color scheme for cards
  const getCardColors = (percentage, isNegativeMetric = false) => {
    const adjustedPercentage = isNegativeMetric ? (100 - percentage) : percentage;
    
    if (adjustedPercentage >= 85) {
      return {
        bg: 'bg-green-500/10',
        border: 'border-green-500/30',
        hover: 'hover:border-green-400',
        text: 'text-green-400'
      };
    } else if (adjustedPercentage >= 60) {
      return {
        bg: 'bg-orange-500/10',
        border: 'border-orange-500/30',
        hover: 'hover:border-orange-400',
        text: 'text-orange-400'
      };
    } else {
      return {
        bg: 'bg-red-500/10',
        border: 'border-red-500/30',
        hover: 'hover:border-red-400',
        text: 'text-red-400'
      };
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'bg-red-500/20 text-red-300 border border-red-500/30';
      case 'Medium': return 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30';
      case 'Low': return 'bg-green-500/20 text-green-300 border border-green-500/30';
      default: return 'bg-slate-600 text-gray-300';
    }
  };

  const handleTaskCardClick = (type, performanceData) => {
    let tasks = [];
    switch(type) {
      case 'completed':
        tasks = performanceData.completedTasks || [];
        break;
      case 'pending':
        tasks = performanceData.pendingTasks || [];
        break;
      case 'overdue':
        tasks = performanceData.overdueTasks || [];
        break;
    }
    setModalTasks(tasks);
    setModalTaskType(type);
    setShowTaskModal(true);
  };

  const getModalTitle = () => {
    switch(modalTaskType) {
      case 'completed': return 'Completed Tasks';
      case 'pending': return 'Pending Tasks';
      case 'overdue': return 'Overdue Tasks';
      default: return 'Tasks';
    }
  };

  // Calendar Component - Dark Theme
  const Calendar = ({ onDateClick, onClose, selectedYear, selectedWeek, isFromCalendar }) => {
    // Initialize display date based on the selected week (not current date)
    const initialDisplayDate = () => {
      const weekRange = getWeekDateRange(selectedYear, selectedWeek);
      return weekRange.start;
    };
    const [displayDate, setDisplayDate] = useState(initialDisplayDate);
    const { weeks, month, year } = generateCalendar(displayDate);
    const calendarRef = useRef(null);
    
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (calendarRef.current && !calendarRef.current.contains(event.target)) {
          onClose();
        }
      };
      
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    const navigateMonth = (direction) => {
      const newDate = new Date(displayDate);
      newDate.setMonth(newDate.getMonth() + direction);
      
      const today = new Date();
      const maxDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      
      if (direction > 0 && newDate > maxDate) {
        return;
      }
      
      setDisplayDate(newDate);
    };
    
    const isSelectedWeek = (date) => {
      const year = date.getFullYear();
      const weekNum = getWeekNumber(date, year);
      return year === selectedYear && weekNum === selectedWeek;
    };
    
    return (
      <div className="relative">
        <div className={`absolute z-[9999] mt-2 ${isFromCalendar ? 'left-0' : 'right-0'}`}>
          <div 
            ref={calendarRef}
            className="bg-slate-800 rounded-xl shadow-xl border border-slate-600 p-6 w-80"
          >
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => navigateMonth(-1)}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h3 className="text-lg font-semibold text-white">
                {monthNames[month]} {year}
              </h3>
              <button
                onClick={() => navigateMonth(1)}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Weekday Headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, index) => (
                <div key={index} className="text-center text-xs font-semibold text-gray-400 py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="space-y-1">
              {weeks.map((week, weekIndex) => (
                <div key={weekIndex} className="grid grid-cols-7 gap-1">
                  {week.map((date, dateIndex) => {
                    const isCurrentMonth = date.getMonth() === month;
                    const isToday = date.toDateString() === new Date().toDateString();
                    const isSelected = isSelectedWeek(date);
                    const isFutureDate = date > new Date();
                    
                    return (
                      <button
                        key={dateIndex}
                        onClick={() => !isFutureDate && onDateClick(date)}
                        disabled={isFutureDate}
                        className={`
                          p-2 text-sm rounded-lg transition-all duration-200 hover:scale-105
                          ${isFutureDate 
                            ? 'text-gray-600 cursor-not-allowed opacity-50' 
                            : isCurrentMonth 
                              ? 'text-white hover:bg-slate-700' 
                              : 'text-gray-500'
                          }
                          ${isSelected ? 'bg-purple-600 text-white hover:bg-purple-700' : ''}
                          ${isToday && !isSelected ? 'ring-2 ring-purple-400' : ''}
                        `}
                      >
                        {date.getDate()}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-900 -m-8 p-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 rounded-xl p-6 text-white mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold mb-1">Team Performance</h1>
        <p className="text-purple-200">Comprehensive performance analysis and productivity tracking</p>
      </div>

      {/* Filters Section - Dark Theme */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-4 mb-6 overflow-visible">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-white ml-3">Filters & Date Range</h2>
          </div>
          
          <button
            onClick={goToCurrentWeek}
            className="bg-purple-600 hover:bg-purple-500 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 text-sm flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
            </svg>
            Current Week
          </button>
        </div>
        
        {/* User Selector - Pill Style */}
        <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-bold text-gray-300 flex items-center">
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Select Team Members ({selectedUsers.length} selected)
            </h4>
            <button
              onClick={handleSelectAll}
              className="text-xs text-purple-400 hover:text-purple-300 font-medium transition-colors"
            >
              {selectedUsers.length === users.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {users.map(user => (
              <button
                key={user.id}
                onClick={() => handleUserToggle(user.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                  selectedUsers.includes(user.id)
                    ? 'bg-purple-600 text-white shadow-lg'
                    : 'bg-slate-800 text-gray-400 hover:text-white hover:bg-slate-700 border border-slate-600'
                }`}
              >
                {user.full_name || user.username}
              </button>
            ))}
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* From Filter */}
          <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600 relative">
            <h4 className="text-xs font-bold text-gray-300 mb-2 flex items-center">
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
              </svg>
              From Date
            </h4>
            <div className="relative">
              <button
                onClick={() => setShowFromCalendar(true)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-left flex items-center justify-between hover:bg-slate-700"
              >
                <span className="truncate">{formatWeekDisplay(fromYear, fromWeek)}</span>
                <svg className="w-4 h-4 text-gray-400 ml-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
                </svg>
              </button>
              
              {showFromCalendar && (
                <Calendar
                  onDateClick={(date) => handleDateClick(date, true)}
                  onClose={() => setShowFromCalendar(false)}
                  selectedYear={fromYear}
                  selectedWeek={fromWeek}
                  isFromCalendar={true}
                />
              )}
            </div>
          </div>

          {/* To Filter */}
          <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600 relative">
            <h4 className="text-xs font-bold text-gray-300 mb-2 flex items-center">
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
              </svg>
              To Date
            </h4>
            <div className="relative">
              <button
                onClick={() => setShowToCalendar(true)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-left flex items-center justify-between hover:bg-slate-700"
              >
                <span className="truncate">{formatWeekDisplay(toYear, toWeek)}</span>
                <svg className="w-4 h-4 text-gray-400 ml-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
                </svg>
              </button>
              
              {showToCalendar && (
                <Calendar
                  onDateClick={(date) => handleDateClick(date, false)}
                  onClose={() => setShowToCalendar(false)}
                  selectedYear={toYear}
                  selectedWeek={toWeek}
                  isFromCalendar={false}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      {selectedUsers.length === 0 ? (
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-12 text-center">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-purple-500/30">
              <svg className="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Select Team Members</h3>
            <p className="text-gray-400">Choose one or more team members from the pills above to view their performance metrics</p>
          </div>
        </div>
      ) : isLoading && performanceDataList.length === 0 ? (
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-12 text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mb-4"></div>
          <p className="text-gray-300">Loading performance data for {selectedUsers.length} team member{selectedUsers.length > 1 ? 's' : ''}...</p>
          <p className="text-sm text-gray-500 mt-2">Calculating metrics...</p>
        </div>
      ) : (
        <>
          {/* Information Section */}
          <div className="bg-purple-500/10 rounded-xl border border-purple-500/30 p-5 mb-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-sm font-semibold text-white mb-2">Performance Score Calculation</h3>
                <p className="text-xs text-gray-300 mb-3">
                  The overall score is calculated as an average of <span className="font-semibold text-purple-300">Attendance Score (50%)</span> and <span className="font-semibold text-purple-300">Task Score (50%)</span>. 
                  Attendance considers days worked, punctuality, and perfect days. Task score evaluates completion rate and overdue penalties.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div>
                    <h4 className="text-xs font-semibold text-white mb-1">Card Color Coding:</h4>
                    <div className="space-y-1">
                      <div className="flex items-center text-xs">
                        <div className="w-3 h-3 rounded bg-green-500/20 border border-green-500/30 mr-2"></div>
                        <span className="text-gray-300"><span className="font-medium text-green-400">Green:</span> ≥85% (Excellent)</span>
                      </div>
                      <div className="flex items-center text-xs">
                        <div className="w-3 h-3 rounded bg-orange-500/20 border border-orange-500/30 mr-2"></div>
                        <span className="text-gray-300"><span className="font-medium text-orange-400">Amber:</span> 60-84% (Good)</span>
                      </div>
                      <div className="flex items-center text-xs">
                        <div className="w-3 h-3 rounded bg-red-500/20 border border-red-500/30 mr-2"></div>
                        <span className="text-gray-300"><span className="font-medium text-red-400">Red:</span> &lt;60% (Needs Attention)</span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-xs font-semibold text-white mb-1">Interactive Cards:</h4>
                    <p className="text-xs text-gray-300">
                      Click on <span className="font-medium text-purple-300">Completed Tasks</span>, <span className="font-medium text-purple-300">Overdue Tasks</span>, or <span className="font-medium text-purple-300">Pending Tasks</span> cards to view detailed task lists.
                    </p>
                  </div>
                </div>
                
                <p className="text-xs text-gray-400 italic">
                  Note: Scores are only calculated for complete week periods. Current or incomplete weeks will show "N/A".
                </p>
              </div>
            </div>
          </div>

          {/* Performance Cards */}
          <div className="space-y-6">
          {performanceDataList.map((performanceData, index) => (
            <div key={index} className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
              {/* Header with User Name */}
              <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mr-3 overflow-hidden">
                      {performanceData.userPhotoUrl ? (
                        <img 
                          src={performanceData.userPhotoUrl} 
                          alt={performanceData.userName}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-lg font-bold text-white">
                          {performanceData.userName.charAt(0)}
                        </span>
                      )}
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">{performanceData.userName}</h2>
                      <p className="text-sm text-purple-200">{performanceData.dateRange.from} - {performanceData.dateRange.to}</p>
                    </div>
                  </div>
                  <div className="text-right px-4 py-2 bg-white/20 rounded-lg backdrop-blur-sm">
                    <p className="text-xs font-semibold text-purple-200 uppercase">Score</p>
                    <p className="text-3xl font-bold text-white">
                      {performanceData.productivity.overallScore}%
                    </p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-5 space-y-4">
                {/* Work Performance Section */}
                <div>
                  <h3 className="text-sm font-bold text-white mb-2 flex items-center">
                    <svg className="w-4 h-4 mr-1.5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    Work Performance
                  </h3>
                
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                  {(() => {
                    const colors = getCardColors(performanceData.tasks.completionRate);
                    return (
                      <button
                        onClick={() => handleTaskCardClick('completed', performanceData)}
                        className={`${colors.bg} rounded-lg p-3 border ${colors.border} ${colors.hover} hover:shadow-md transition-all cursor-pointer text-left`}
                      >
                        <p className="text-xs text-gray-400">Completed Tasks</p>
                        <p className="text-xl font-bold text-white">{performanceData.tasks.completed}/{performanceData.tasks.totalAssigned}</p>
                        <p className={`text-xs ${colors.text} font-medium`}>{performanceData.tasks.completionRate}%</p>
                      </button>
                    );
                  })()}

                  {(() => {
                    const colors = getCardColors(performanceData.tasks.overduePercentage, true);
                    return (
                      <button
                        onClick={() => handleTaskCardClick('overdue', performanceData)}
                        className={`${colors.bg} rounded-lg p-3 border ${colors.border} ${colors.hover} hover:shadow-md transition-all cursor-pointer text-left`}
                      >
                        <p className="text-xs text-gray-400">Overdue Tasks</p>
                        <p className="text-xl font-bold text-white">{performanceData.tasks.overdue}/{performanceData.tasks.totalAssigned}</p>
                        <p className={`text-xs ${colors.text} font-medium`}>{performanceData.tasks.overduePercentage}%</p>
                      </button>
                    );
                  })()}
                  
                  {(() => {
                    const pendingPercentage = (performanceData.tasks.pending / performanceData.tasks.totalAssigned * 100);
                    const colors = getCardColors(pendingPercentage, true);
                    return (
                      <button
                        onClick={() => handleTaskCardClick('pending', performanceData)}
                        className={`${colors.bg} rounded-lg p-3 border ${colors.border} ${colors.hover} hover:shadow-md transition-all cursor-pointer text-left`}
                      >
                        <p className="text-xs text-gray-400">Pending Tasks</p>
                        <p className="text-xl font-bold text-white">{performanceData.tasks.pending}/{performanceData.tasks.totalAssigned}</p>
                        <p className={`text-xs ${colors.text} font-medium`}>{pendingPercentage.toFixed(1)}%</p>
                      </button>
                    );
                  })()}
                  
                  {(() => {
                    const daysWithPhotos = performanceData.photoUpload?.daysWithPhotos || 0;
                    const photoUploadPercentage = (daysWithPhotos / performanceData.summary.totalDaysExpected * 100);
                    const colors = getCardColors(photoUploadPercentage);
                    
                    return (
                      <div className={`${colors.bg} rounded-lg p-3 border ${colors.border}`}>
                        <p className="text-xs text-gray-400">Work Photo Uploads</p>
                        <p className="text-xl font-bold text-white">{daysWithPhotos}/{performanceData.summary.totalDaysExpected}</p>
                        <p className={`text-xs ${colors.text} font-medium`}>{photoUploadPercentage.toFixed(1)}%</p>
                      </div>
                    );
                  })()}
                  
                  {(() => {
                    const perfectPercentage = (performanceData.attendance.perfectDays / performanceData.summary.totalDaysExpected * 100);
                    const colors = getCardColors(perfectPercentage);
                    return (
                      <div className={`${colors.bg} rounded-lg p-3 border ${colors.border}`}>
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-gray-400">Perfect Days</p>
                          <div className="group relative">
                            <svg className="w-4 h-4 text-gray-500 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                            <div className="invisible group-hover:visible absolute right-0 bottom-full mb-2 w-56 bg-slate-900 text-white text-xs rounded-lg p-3 shadow-lg z-50 border border-slate-600">
                              <p className="font-semibold mb-2">Perfect Day requires:</p>
                              <p className="mb-1">✓ On-time clock-in</p>
                              <p className="mb-1">✓ Full expected hours worked</p>
                              <p>✓ Photos uploaded</p>
                            </div>
                          </div>
                        </div>
                        <p className="text-xl font-bold text-white">{performanceData.attendance.perfectDays}/{performanceData.summary.totalDaysExpected}</p>
                        <p className={`text-xs ${colors.text} font-medium`}>{perfectPercentage.toFixed(1)}%</p>
                      </div>
                    );
                  })()}
                </div>
                </div>

                {/* Attendance */}
                <div>
                  <h3 className="text-sm font-bold text-white mb-2 flex items-center">
                    <svg className="w-4 h-4 mr-1.5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Attendance
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
                  {(() => {
                    const colors = getCardColors(performanceData.summary.hoursPercentage);
                    return (
                      <div className={`${colors.bg} rounded-lg p-3 border ${colors.border}`}>
                        <p className="text-xs text-gray-400">Hours Worked</p>
                        <p className="text-xl font-bold text-white">{performanceData.summary.totalHoursWorked}/{performanceData.summary.expectedTotalHours}</p>
                        <p className={`text-xs ${colors.text} font-medium`}>{performanceData.summary.hoursPercentage}%</p>
                      </div>
                    );
                  })()}

                  {(() => {
                    const colors = getCardColors(performanceData.summary.attendanceRate);
                    return (
                      <div className={`${colors.bg} rounded-lg p-3 border ${colors.border}`}>
                        <p className="text-xs text-gray-400">Days Worked</p>
                        <p className="text-xl font-bold text-white">{performanceData.summary.totalDaysWorked}/{performanceData.summary.totalDaysExpected}</p>
                        <p className={`text-xs ${colors.text} font-medium`}>{performanceData.summary.attendanceRate}%</p>
                      </div>
                    );
                  })()}

                  {(() => {
                    const onTimePercentage = (performanceData.attendance.onTime / performanceData.summary.totalDaysExpected * 100);
                    const colors = getCardColors(onTimePercentage);
                    return (
                      <div className={`${colors.bg} rounded-lg p-3 border ${colors.border}`}>
                        <p className="text-xs text-gray-400">On Time</p>
                        <p className="text-xl font-bold text-white">{performanceData.attendance.onTime}/{performanceData.summary.totalDaysExpected}</p>
                        <p className={`text-xs ${colors.text} font-medium`}>{onTimePercentage.toFixed(1)}%</p>
                      </div>
                    );
                  })()}

                  {(() => {
                    const latePercentage = (performanceData.attendance.late / performanceData.summary.totalDaysExpected * 100);
                    const colors = getCardColors(latePercentage, true);
                    return (
                      <div className={`${colors.bg} rounded-lg p-3 border ${colors.border}`}>
                        <p className="text-xs text-gray-400">Late</p>
                        <p className="text-xl font-bold text-white">{performanceData.attendance.late}/{performanceData.summary.totalDaysExpected}</p>
                        <p className={`text-xs ${colors.text} font-medium`}>{latePercentage.toFixed(1)}%</p>
                      </div>
                    );
                  })()}

                    {(() => {
                      const forcedPercentage = (performanceData.attendance.forcedClockOuts / performanceData.summary.totalDaysExpected * 100);
                      const colors = getCardColors(forcedPercentage, true);
                      return (
                        <div className={`${colors.bg} rounded-lg p-3 border ${colors.border}`}>
                          <p className="text-xs text-gray-400">Forced Clock-Outs</p>
                          <p className="text-xl font-bold text-white">{performanceData.attendance.forcedClockOuts}/{performanceData.summary.totalDaysExpected}</p>
                          <p className={`text-xs ${colors.text} font-medium`}>{forcedPercentage.toFixed(1)}%</p>
                        </div>
                      );
                    })()}

                    {(() => {
                      const earlyPercentage = (performanceData.attendance.earlyClockOuts / performanceData.summary.totalDaysExpected * 100);
                      const colors = getCardColors(earlyPercentage, true);
                      return (
                        <div className={`${colors.bg} rounded-lg p-3 border ${colors.border}`}>
                          <p className="text-xs text-gray-400">Early Clock-Outs</p>
                          <p className="text-xl font-bold text-white">{performanceData.attendance.earlyClockOuts}/{performanceData.summary.totalDaysExpected}</p>
                          <p className={`text-xs ${colors.text} font-medium`}>{earlyPercentage.toFixed(1)}%</p>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          ))}
          
          {isLoading && performanceDataList.length < selectedUsers.length && (
            <div className="bg-slate-800 rounded-xl border-2 border-dashed border-slate-600 p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mb-3"></div>
              <p className="text-sm text-gray-400">
                Loading {performanceDataList.length + 1} of {selectedUsers.length}...
              </p>
            </div>
          )}
        </div>
        </>
      )}

      {/* Task Details Modal - Dark Theme */}
      {showTaskModal && (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowTaskModal(false)}
        >
          <div 
            className="bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden border border-slate-700"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{getModalTitle()}</h3>
                    <p className="text-sm text-purple-200">{modalTasks.length} task{modalTasks.length !== 1 ? 's' : ''}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowTaskModal(false)}
                  className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-all duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-100px)]">
              {modalTasks.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">No {modalTaskType} tasks</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {modalTasks.map(task => (
                    <div key={task.id} className="bg-slate-800 p-4 rounded-lg border border-slate-700 hover:border-slate-600 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-white mb-1">{task.title}</p>
                          <div className="flex items-center gap-3 text-xs text-gray-400">
                            {task.dueDate && (
                              <span>Due: {task.dueDate}</span>
                            )}
                            {task.completedDate && (
                              <span>Completed: {task.completedDate}</span>
                            )}
                            {task.daysOverdue !== undefined && (
                              <span className="text-red-400 font-medium">{task.daysOverdue} days overdue</span>
                            )}
                            {task.daysRemaining !== undefined && (
                              <span className={task.daysRemaining <= 3 ? 'text-red-400 font-medium' : 'text-gray-400'}>
                                {task.daysRemaining} days remaining
                              </span>
                            )}
                          </div>
                        </div>
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(task.priority)}`}>
                          {task.priority}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAttendanceTaskPage;
